/**
 * The step-up card, the step-up dialog and the provider that opens it (kernel apps, WS-9b,
 * `design-docs/WS-9.md` section 3.4).
 *
 * `StepUpCard` reads one CP step-up request from cp-auth-api and shows only server fields, as text
 * nodes: the summary title and lines, the method, the path and the expiry. A `role-elevation` card
 * shows the title and each line as one sentence (`StepUpMode`). While the request is
 * `pending` the operator can approve it with a passkey, deny it or cancel. `StepUpDialog` puts the
 * card in a modal. `StepUpProvider` holds one open dialog at a time, and `useStepUp()` gives a pane
 * the `runWithStepUp` of `step-up.ts` with that dialog as its approver. `StepUpPage` renders the card
 * on its own for the link that `zion-app release` opens.
 *
 * Security rules (`design-docs/WS-9.md` section 5): the card shows server text only, and this file
 * holds no `dangerouslySetInnerHTML` (S-07). The view parse refuses options whose
 * `userVerification` is not `required` before the card offers a passkey (S-09). A 403 hides the
 * approval (S-06).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  startAuthentication,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import type { StepUpPublicKeyOptions, StepUpRequestView, StepUpStatus } from '@zion/sdk-contracts';
import { ApiError } from '../http/client';
import { displayMessage } from '../errors';
import { isUserCancellation } from '../../pages/LoginPage';
import { Button } from '../../shell/Button';
import { dataToneClass } from '../../shell/data-tone';
import { focusablesIn, inertSiblings, releaseInert, trapTabTarget } from '../../shell/PasskeyDialog';
import { focusPanel, handleDialogKey, restoreFocus } from '../../panes/dialog-panel';
import {
  denyStepUpRequest,
  getStepUpRequest,
  runWithStepUp,
  submitStepUpAssertion,
  type StepUpApprover,
  type StepUpOutcome,
} from './step-up';

/**
 * The WebAuthn assertion call. The default is `startAuthentication({ optionsJSON })` of
 * `@simplewebauthn/browser`, never raw `navigator.credentials` (CLAUDE.md). A test passes another
 * function only where the browser fake cannot stage the case.
 */
export type Authenticate = (options: PublicKeyCredentialRequestOptionsJSON) => Promise<AuthenticationResponseJSON>;

/**
 * How the card presents one request.
 *
 * - `operation`: the default. The card shows the summary with its labels, the method and path, the
 *   24-hour expiry, and Cancel, Deny and Approve with passkey. Every step-up kind except the role
 *   elevation uses it.
 * - `role-elevation`: the person asked to act as a higher site role (plan 2026-10-22-cp-active-role).
 *   The person started the request, so the card shows the summary title as the one title and each
 *   summary line as one plain sentence, its label then its value ("Act as platform_admin", "For 8 h",
 *   from the lines cp-auth-api writes). It hides the path and the 24-hour expiry, because the summary
 *   carries the length of the elevation. It offers Cancel and Use passkey; Cancel still sends the
 *   deny when `cancelSendsDeny` is set.
 *
 * In both modes an error line is an alert in the critical tone (R-006).
 */
export type StepUpMode = 'operation' | 'role-elevation';

/** The title of a role-elevation card before the request is read, and after a failed read. */
export const ROLE_ELEVATION_FALLBACK_TITLE = 'Act as a site role';

/** Run one write that may owe a step-up, with the provider's dialog as the approver. */
export type RunWithStepUp = <T>(send: (stepUpId: string | null) => Promise<T>) => Promise<T>;

/** The props of `StepUpCard` and `StepUpDialog`. */
export interface StepUpCardProps {
  /** The lower-case UUID of the step-up request. The caller checks it with `isStepUpRequestId`. */
  readonly requestId: string;
  /**
   * Called once, when the request ends for this card. `outcome` is the outcome. `forbidden` is
   * `true` only when the server refused this operator and the request itself stays `pending`. Two
   * answers do that, and each settles `denied`:
   *
   * 1. a 403 on the read, the assertion or the deny (`STEP_UP_FORBIDDEN_TEXT`);
   * 2. a 422 `operator_inactive` on the assertion (`STEP_UP_INACTIVE_TEXT`, R-109).
   *
   * Every other settle passes `false`.
   */
  readonly onSettled: (outcome: StepUpOutcome, forbidden: boolean) => void;
  /** The assertion call. Defaults to `startAuthentication({ optionsJSON })`. */
  readonly authenticate?: Authenticate;
  /**
   * `true` makes Cancel send a deny first, and ignore a failure of the deny. The in-console dialog
   * sets it, because the console opened the request and nothing else waits on it. `StepUpPage`
   * clears it: the terminal that opened the link still waits, and the request expires after 24
   * hours (I-11).
   */
  readonly cancelSendsDeny: boolean;
  /**
   * How the card presents the request. Absent on `StepUpCard`: the mode the request's own subject
   * names, so a `role-elevation` request opened from a link shows as one; `operation` until the view
   * arrives. Absent on `StepUpDialog`: `operation`.
   */
  readonly mode?: StepUpMode;
  /**
   * The id of the role-elevation title, for the dialog's `aria-labelledby`. The card puts it on its
   * one heading in `role-elevation` mode and ignores it in `operation` mode.
   */
  readonly titleId?: string;
}

/** The text that the card shows for each outcome. */
export const STEP_UP_OUTCOME_TEXT: Readonly<Record<StepUpOutcome, string>> = {
  approved: 'Approved.',
  denied: 'Denied.',
  expired: 'This request expired.',
  consumed: 'The operation already used this approval. Start it again.',
  cancelled: 'Cancelled.',
};

/** The text of a 403 on the read or on the assertion: the server refused this operator. */
export const STEP_UP_FORBIDDEN_TEXT = 'You cannot approve this operation';

/**
 * The text of a 422 `operator_inactive` on the assertion: cp-auth-api found the operator absent or
 * not `active` when it tried to approve (R-109). The request stays `pending`, and a retry gets the
 * same answer, so the card offers no control after it.
 */
export const STEP_UP_INACTIVE_TEXT = 'This account cannot approve operations.';

/** The server `message` of the 422 that the CP assertion route answers for an inactive operator. */
const OPERATOR_INACTIVE_MESSAGE = 'operator_inactive';

/**
 * True for an `ApiError` 422 whose body is an object with the string `message` `operator_inactive`.
 * Another status, another message, a `message` array, a non-object body and a value that is not an
 * `ApiError` answer `false`, so an unknown 422 keeps the card's controls. Pure.
 */
function isOperatorInactive(cause: unknown): boolean {
  if (!(cause instanceof ApiError) || cause.status !== 422) return false;
  const body = cause.body;
  if (body === null || typeof body !== 'object' || !('message' in body)) return false;
  return (body as { readonly message: unknown }).message === OPERATOR_INACTIVE_MESSAGE;
}

/** The text of a 400 on the assertion. The request stays `pending`, so the operator can try again. */
export const STEP_UP_NOT_VERIFIED_TEXT = 'The passkey did not verify';

/**
 * The outcome of a view whose status is not `pending`. The `pending` row maps to `expired` and the
 * card never reads it: `stepUpRequestViewSchema` pairs options with `pending` alone, so a `pending`
 * view always takes the options arm.
 */
const OUTCOME_OF_STATUS: Readonly<Record<StepUpStatus, StepUpOutcome>> = {
  pending: 'expired',
  approved: 'approved',
  denied: 'denied',
  expired: 'expired',
  consumed: 'consumed',
};

/** The default assertion call: `startAuthentication({ optionsJSON })` and nothing else. */
function defaultAuthenticate(optionsJSON: PublicKeyCredentialRequestOptionsJSON): Promise<AuthenticationResponseJSON> {
  return startAuthentication({ optionsJSON });
}

/**
 * Send the deny of a cancel when `sendsDeny` is `true`, and ignore its failure. The request then
 * expires on its own after 24 hours (I-11). It never throws.
 */
async function denyOnCancel(requestId: string, sendsDeny: boolean): Promise<void> {
  if (!sendsDeny) return;
  try {
    await denyStepUpRequest(requestId);
  } catch {
    // The cancel stands. A deny that did not land leaves a request that expires unused.
  }
}

type CardState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'pending'; readonly view: StepUpRequestView; readonly options: StepUpPublicKeyOptions }
  | { readonly kind: 'failed'; readonly message: string }
  | { readonly kind: 'settled'; readonly text: string };

/**
 * One step-up request as a card: the server summary, then Approve with passkey, Deny and Cancel.
 *
 * On mount it reads the request. A `pending` view shows the controls. Every other status shows its
 * `STEP_UP_OUTCOME_TEXT` and settles with its outcome. A 404 or a 410 on the read settles
 * `expired`; a 403 shows `STEP_UP_FORBIDDEN_TEXT` and settles `denied` with `forbidden` `true`;
 * another failure shows the message and offers Cancel alone.
 *
 * Approve passes the view's options unchanged to `authenticate`. A cancelled prompt
 * (`NotAllowedError`, `AbortError`) shows no error. The assertion answer settles by its status. A
 * 400 shows `STEP_UP_NOT_VERIFIED_TEXT` and keeps the controls; a 409 reads the request again; a 410
 * settles `expired`; a 403 settles `denied` with `forbidden` `true`. A 422 `operator_inactive`
 * shows `STEP_UP_INACTIVE_TEXT` and settles `denied` with `forbidden` `true` (R-109); another 422
 * shows its message and keeps the controls. Deny settles `denied` after the deny lands. Cancel
 * settles `cancelled`, after a deny when `cancelSendsDeny` is set.
 *
 * Side effects: the GET, assertion and deny requests of `step-up.ts`, and one WebAuthn prompt per
 * Approve press. It calls `onSettled` once, when the request ends for this card.
 */
export function StepUpCard({
  requestId,
  onSettled,
  authenticate = defaultAuthenticate,
  cancelSendsDeny,
  mode,
  titleId,
}: StepUpCardProps): React.JSX.Element {
  const [state, setState] = useState<CardState>({ kind: 'loading' });
  /** The mode the subject of the last view read names, for a caller that passes no mode. */
  const [subjectMode, setSubjectMode] = useState<StepUpMode>('operation');
  /** The summary title of the last view read, so a role-elevation card keeps one title in every state. */
  const [title, setTitle] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  /**
   * End the card with `outcome`. The card then renders no control, and every request that can
   * settle runs while the controls are off (`busy`), so this runs once per card.
   */
  const settle = useCallback((outcome: StepUpOutcome, text: string, forbidden = false) => {
    setState({ kind: 'settled', text });
    onSettledRef.current(outcome, forbidden);
  }, []);

  const applyView = useCallback(
    (view: StepUpRequestView) => {
      setTitle(view.summary.title);
      const subject = view.request.subject;
      setSubjectMode(subject.plane === 'cp' && subject.operation === 'role-elevation' ? 'role-elevation' : 'operation');
      const options = view.publicKeyOptions;
      if (options !== null) {
        setState({ kind: 'pending', view, options });
        return;
      }
      const outcome = OUTCOME_OF_STATUS[view.request.status];
      settle(outcome, STEP_UP_OUTCOME_TEXT[outcome]);
    },
    [settle],
  );

  /** The answer to a refusal that ends the request for this operator, or `false` for any other error. */
  const settleOnRefusal = useCallback(
    (cause: unknown): boolean => {
      if (!(cause instanceof ApiError)) return false;
      if (cause.status === 403) {
        settle('denied', STEP_UP_FORBIDDEN_TEXT, true);
        return true;
      }
      if (cause.status === 404 || cause.status === 410) {
        settle('expired', STEP_UP_OUTCOME_TEXT.expired);
        return true;
      }
      return false;
    },
    [settle],
  );

  const load = useCallback(async () => {
    try {
      applyView(await getStepUpRequest(requestId));
    } catch (cause) {
      if (!settleOnRefusal(cause)) {
        setState({ kind: 'failed', message: displayMessage(cause, 'The step-up request could not be read.') });
      }
    }
  }, [applyView, requestId, settleOnRefusal]);

  useEffect(() => {
    void load();
  }, [load]);

  async function approve(options: StepUpPublicKeyOptions): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const response = await authenticate(options);
      applyView(await submitStepUpAssertion(requestId, response));
    } catch (cause) {
      if (isUserCancellation(cause) || settleOnRefusal(cause)) return;
      if (isOperatorInactive(cause)) {
        settle('denied', STEP_UP_INACTIVE_TEXT, true);
      } else if (cause instanceof ApiError && cause.status === 400) {
        setError(STEP_UP_NOT_VERIFIED_TEXT);
      } else if (cause instanceof ApiError && cause.status === 409) {
        await load();
      } else {
        setError(displayMessage(cause, 'The approval did not complete.'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function deny(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await denyStepUpRequest(requestId);
      settle('denied', STEP_UP_OUTCOME_TEXT.denied);
    } catch (cause) {
      if (!settleOnRefusal(cause)) setError(displayMessage(cause, 'The deny did not complete.'));
    } finally {
      setBusy(false);
    }
  }

  async function cancel(): Promise<void> {
    setBusy(true);
    await denyOnCancel(requestId, cancelSendsDeny);
    settle('cancelled', STEP_UP_OUTCOME_TEXT.cancelled);
  }

  const cancelButton = (
    <Button tone="default" size="md" disabled={busy} onClick={() => void cancel()} data-testid="step-up-cancel">
      Cancel
    </Button>
  );

  const elevation = (mode ?? subjectMode) === 'role-elevation';
  // Every error line takes the critical tone, in both modes (R-006).
  const errorClass = `max-w-[100ch] text-sm ${dataToneClass('bad', 'text')}`;
  const elevationTitle = (
    <h2 id={titleId} data-testid="step-up-title" className="text-base font-semibold text-primary">
      {title ?? ROLE_ELEVATION_FALLBACK_TITLE}
    </h2>
  );
  /** In role-elevation mode every state sits under the one title; operation mode renders the body alone. */
  const framed = (body: React.JSX.Element): React.JSX.Element =>
    elevation ? (
      <div className="flex flex-col gap-4">
        {elevationTitle}
        {body}
      </div>
    ) : (
      body
    );

  if (state.kind === 'loading') {
    return framed(
      <p role="status" data-testid="step-up-loading" className="text-sm text-secondary">
        Reading the request.
      </p>,
    );
  }
  if (state.kind === 'settled') {
    return framed(
      <p role="status" data-testid="step-up-settled" className="text-sm text-primary">
        {state.text}
      </p>,
    );
  }
  if (state.kind === 'failed') {
    return framed(
      <div data-testid="step-up-card" className="flex flex-col gap-4">
        <p role="alert" data-testid="step-up-error" className={errorClass}>
          {state.message}
        </p>
        <div className="flex justify-end">{cancelButton}</div>
      </div>,
    );
  }

  const { view, options } = state;
  return (
    <div data-testid="step-up-card" className="flex flex-col gap-4">
      {elevation ? (
        elevationTitle
      ) : (
        <h2 data-testid="step-up-title" className="text-base font-semibold text-primary">
          {view.summary.title}
        </h2>
      )}
      {elevation ? (
        // A polite status, so a screen reader hears the role and the length when the read answers.
        <div role="status" aria-live="polite" data-testid="step-up-summary" className="flex flex-col gap-2 text-sm text-primary">
          {view.summary.lines.map((line, index) => (
            <p key={`line-${String(index)}`}>
              {line.label} {line.value}
            </p>
          ))}
        </div>
      ) : (
        <>
          <dl data-testid="step-up-summary" className="flex flex-col gap-2 text-sm">
            {view.summary.lines.map((line, index) => (
              <div key={`line-${String(index)}`} className="flex flex-col">
                <dt className="text-xs text-secondary">{line.label}</dt>
                <dd className="text-primary">{line.value}</dd>
              </div>
            ))}
          </dl>
          <p data-testid="step-up-operation" className="font-mono text-xs text-secondary">
            {view.request.method} {view.request.path}
          </p>
          <p data-testid="step-up-expiry" className="text-xs text-secondary">
            Expires at {view.request.expiresAt}
          </p>
        </>
      )}
      {error === null ? null : (
        <p role="alert" data-testid="step-up-error" className={errorClass}>
          {error}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {cancelButton}
        {elevation ? null : (
          <Button tone="danger" size="md" disabled={busy} onClick={() => void deny()} data-testid="step-up-deny">
            Deny
          </Button>
        )}
        <Button
          tone="primary"
          size="md"
          disabled={busy}
          onClick={() => void approve(options)}
          data-testid="step-up-approve"
        >
          {elevation ? 'Use passkey' : 'Approve with passkey'}
        </Button>
      </div>
    </div>
  );
}

/** The id that names the dialog for `aria-labelledby`. */
const DIALOG_TITLE_ID = 'step-up-dialog-title';

/**
 * `StepUpCard` in a modal, with the five modal parts of `PasskeyDialog.tsx`:
 *
 * 1. a portal into a host that it appends to `<body>`;
 * 2. `inert` on every other body child, released before `onSettled` runs;
 * 3. focus on the dialog at mount;
 * 4. a Tab trap through `trapTabTarget`;
 * 5. focus back on the element that held it at mount.
 *
 * An `approved` or a `cancelled` settle closes the dialog at once. Every other settle (`denied`,
 * `expired`, `consumed`, and a refusal with `forbidden` `true`) keeps the dialog open on the card's
 * settled text, `STEP_UP_FORBIDDEN_TEXT` and `STEP_UP_INACTIVE_TEXT` included, with a Close button that takes the focus. Close
 * or Escape then closes it with that outcome. So the operator reads how the step-up ended before
 * the dialog goes (WS-9b-R17, S-06).
 *
 * Escape on the open card runs Cancel; Escape on the settled text sends no deny. It calls
 * `onSettled` at most once, and passes the card's `forbidden` through.
 *
 * In `role-elevation` mode the dialog shows no "Approve this operation" line: the card's one
 * heading names the dialog.
 */
export function StepUpDialog({
  requestId,
  onSettled,
  authenticate,
  cancelSendsDeny,
  mode = 'operation',
}: StepUpCardProps): React.JSX.Element {
  const [host] = useState(() => document.createElement('div'));
  const [opener] = useState(() => document.activeElement);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const releasedRef = useRef<readonly Element[]>([]);
  const closedRef = useRef(false);
  const [held, setHeld] = useState<{ readonly outcome: StepUpOutcome; readonly forbidden: boolean } | null>(null);

  useEffect(() => {
    host.setAttribute('data-step-up-portal', '');
    document.body.append(host);
    releasedRef.current = inertSiblings(document.body, host);
    return () => {
      releaseInert(releasedRef.current);
      host.remove();
    };
  }, [host]);

  useEffect(() => {
    focusPanel(cardRef.current);
  }, []);

  const close = useCallback(
    (outcome: StepUpOutcome, forbidden: boolean) => {
      if (closedRef.current) return;
      closedRef.current = true;
      releaseInert(releasedRef.current);
      onSettled(outcome, forbidden);
      restoreFocus(opener);
    },
    [onSettled, opener],
  );

  /** The card settled: close on `approved` and `cancelled`, else hold the settled text. */
  const onCardSettled = useCallback(
    (outcome: StepUpOutcome, forbidden: boolean) => {
      if (outcome === 'approved' || outcome === 'cancelled') close(outcome, forbidden);
      else setHeld({ outcome, forbidden });
    },
    [close],
  );

  useEffect(() => {
    if (held !== null) focusPanel(closeRef.current);
  }, [held]);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    handleDialogKey(event.key, () => {
      if (held !== null) close(held.outcome, held.forbidden);
      else void denyOnCancel(requestId, cancelSendsDeny).then(() => close('cancelled', false));
    });
    if (event.key !== 'Tab') return;
    const target = trapTabTarget(focusablesIn(cardRef.current), document.activeElement, event.shiftKey);
    if (target === null) return;
    event.preventDefault();
    target.focus();
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="step-up-overlay">
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={DIALOG_TITLE_ID}
        data-testid="step-up-dialog"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col gap-4 overflow-y-auto rounded-xl border border-border-subtle bg-surface-elevated p-6 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
      >
        {mode === 'role-elevation' ? null : (
          <p id={DIALOG_TITLE_ID} className="text-sm font-semibold text-secondary">
            Approve this operation
          </p>
        )}
        <StepUpCard
          requestId={requestId}
          onSettled={onCardSettled}
          authenticate={authenticate}
          cancelSendsDeny={cancelSendsDeny}
          mode={mode}
          titleId={DIALOG_TITLE_ID}
        />
        {held === null ? null : (
          <div className="flex justify-end">
            <Button
              ref={closeRef}
              tone="default"
              size="md"
              onClick={() => close(held.outcome, held.forbidden)}
              data-testid="step-up-close"
            >
              Close
            </Button>
          </div>
        )}
      </div>
    </div>,
    host,
  );
}

/** The provider's approver: one request, shown in the given mode. */
type ModeApprover = (requestId: string, mode: StepUpMode) => Promise<StepUpOutcome>;

const StepUpContext = createContext<ModeApprover | null>(null);

interface OpenStepUp {
  readonly seq: number;
  readonly requestId: string;
  readonly mode: StepUpMode;
  readonly resolve: (outcome: StepUpOutcome) => void;
}

/**
 * Holds one open `StepUpDialog` for the panes under it. A second step-up waits until the first one
 * settles, in call order. The dialog sends a deny on Cancel (`cancelSendsDeny: true`). An outcome
 * other than `approved` or `cancelled` resolves when the operator closes the settled text, so the
 * pane's write fails after the operator has read how the step-up ended.
 *
 * `CpAdminApp` renders it inside `PermissionProvider`, around `AuthenticatedShell`.
 */
export function StepUpProvider({
  children,
  authenticate,
}: {
  readonly children: React.ReactNode;
  readonly authenticate?: Authenticate;
}): React.JSX.Element {
  const queueRef = useRef<readonly OpenStepUp[]>([]);
  const seqRef = useRef(0);
  const [head, setHead] = useState<OpenStepUp | null>(null);

  const approve = useCallback<ModeApprover>(
    (requestId, mode) =>
      new Promise<StepUpOutcome>((resolve) => {
        seqRef.current += 1;
        const item: OpenStepUp = { seq: seqRef.current, requestId, mode, resolve };
        queueRef.current = [...queueRef.current, item];
        if (queueRef.current.length === 1) setHead(item);
      }),
    [],
  );

  function settleHead(open: OpenStepUp, outcome: StepUpOutcome): void {
    queueRef.current = queueRef.current.slice(1);
    open.resolve(outcome);
    setHead(queueRef.current[0] ?? null);
  }

  return (
    <StepUpContext.Provider value={approve}>
      {children}
      {head === null ? null : (
        <StepUpDialog
          key={head.seq}
          requestId={head.requestId}
          onSettled={(outcome) => settleHead(head, outcome)}
          authenticate={authenticate}
          cancelSendsDeny
          mode={head.mode}
        />
      )}
    </StepUpContext.Provider>
  );
}

/**
 * The `runWithStepUp` of `step-up.ts`, bound to the dialog of the nearest `StepUpProvider`. The
 * dialog presents each request in `mode` (default `operation`). The function keeps its identity
 * while the provider lives and the mode stays the same.
 *
 * @throws Error('useStepUp needs a StepUpProvider') during render outside a provider.
 */
export function useStepUp(mode: StepUpMode = 'operation'): RunWithStepUp {
  const approve = useContext(StepUpContext);
  const run = useMemo<RunWithStepUp | null>(() => {
    if (approve === null) return null;
    const approver: StepUpApprover = (requestId) => approve(requestId, mode);
    return <T,>(send: (stepUpId: string | null) => Promise<T>): Promise<T> => runWithStepUp(send, approver);
  }, [approve, mode]);
  if (run === null) throw new Error('useStepUp needs a StepUpProvider');
  return run;
}
